import type { ReactElement } from "react";
import { EinheitFeld, TextFeld, WahlFeld, ZahlFeld, feldId } from "./felder.js";
import { OHNE_FAEHIGKEIT } from "./Marke.js";
import {
  fussSpaltenAus, fussSumme, mitFussBreite, mitFussSpalten, mitFussSpaltenzahl, mitWert,
  wertAn, zahlAus,
} from "../layoutEntwurf.js";
import type { Entwurf, FussSpalte } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";

/**
 * Register „Seite": die vier Ränder in mm (5..40), Systemwert als Platzhalter — und, wenn die
 * Engine Belegdesign Etappe 2 kennt (`faehig`), Kopf- und Fußbereich je Seitentyp, der Beginn
 * der Positionstabelle und der Fuß in Spalten (Entscheid 75).
 */
const RAENDER = [
  { id: "oben", label: "Oberer Rand" }, { id: "rechts", label: "Rechter Rand" },
  { id: "unten", label: "Unterer Rand" }, { id: "links", label: "Linker Rand" },
] as const;

const SEITEN = [
  { id: "ersteSeiteMm", text: "Seite 1" }, { id: "folgeseitenMm", text: "Folgeseiten" },
] as const;

const PLATZHALTER = [
  { name: "{firmenfuss}", text: "Firmenfuß: auf Seite 1 Name · Anschrift, USt-IdNr., IBAN · BIC und die Fußzeile, auf Folgeseiten die Fußzeile" },
  { name: "{belegnummer}", text: "Belegnummer (bei Entwürfen „Entwurf“)" },
  { name: "{seite}", text: "aktuelle Seitenzahl" },
  { name: "{seiten}", text: "Gesamtzahl der Seiten" },
  { name: "{fusszeile}", text: "die bisherige Fußzeile (Register Texte)" },
] as const;

/** Ein mm-Wert aus dem Entwurf („18.5mm“), sonst der Systemwert; Zahl in mm. */
function mmWert(entwurf: Entwurf, pfad: readonly string[], system: string): number {
  const n = Number(zahlAus(wertAn(entwurf, pfad), "mm"));
  return zahlAus(wertAn(entwurf, pfad), "mm") !== "" && Number.isFinite(n)
    ? n : Number(zahlAus(system, "mm"));
}

function mmText(mm: number): string {
  return String(Math.round(mm * 10) / 10).replace(".", ",");
}

/** Befunde mit diesem Pfadanfang, die kein Feld der Gruppe trägt — sie sollen nicht
 *  verschwinden, obwohl der Reiter sie zählt. */
function restBefunde(fehler: Record<string, string>, anfang: string, behandelt: (pfad: string) => boolean):
    [string, string][] {
  return Object.entries(fehler).filter(([pfad]) =>
    (pfad === anfang || pfad.startsWith(`${anfang}.`) || pfad.startsWith(`${anfang}[`))
    && !behandelt(pfad));
}

function Befunde(p: { liste: [string, string][] }): ReactElement | null {
  return p.liste.length === 0 ? null : (
    <>
      {p.liste.map(([pfad, text]) => (
        <span key={pfad} className="lay__feldfehler" role="alert">{text}</span>
      ))}
    </>
  );
}

export function SeitePanel(p: PanelProps & { faehig?: boolean | undefined }): ReactElement {
  return (
    <div className="lay__panel">
      <p className="lay__panelhinweis">Ränder in Millimetern ab Blattkante (5 bis 40 mm).</p>
      {RAENDER.map((r) => {
        const pfad = `seitenraender.${r.id}`;
        return (
          <EinheitFeld key={r.id} id={feldId(pfad)} label={r.label} einheit="mm"
            wert={wertAn(p.entwurf, ["seitenraender", r.id])}
            systemwert={p.stand.katalog.seitenraender[r.id]}
            fehler={p.feldFehler[pfad]}
            onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, ["seitenraender", r.id], neu))}
            onVerlassen={p.onFeldVerlassen} />
        );
      })}
      {p.faehig === true
        ? <SeiteEtappe2 {...p} />
        : <p className="lay__hinweis">{OHNE_FAEHIGKEIT} — Kopf- und Fußbereich, Beginn der
            Positionstabelle und Fußspalten stehen bereit, sobald sie aktualisiert ist.</p>}
    </div>
  );
}

/** Kopf- und Fußbereich: je Seitentyp eine Zahl; leer = Systemwert (der Seitenrand). */
function Bereich(p: PanelProps & { art: "kopfbereich" | "fussbereich" }): ReactElement {
  const kopf = p.art === "kopfbereich";
  const katalog = p.stand.katalog[p.art];
  const randId = kopf ? "oben" : "unten";
  const rand = mmWert(p.entwurf, ["seitenraender", randId], p.stand.katalog.seitenraender[randId]);
  const untergrenze = Math.max(rand, katalog.minMm);
  const name = kopf ? "Kopfbereich" : "Fußbereich";
  const behandelt = (pfad: string): boolean => SEITEN.some((s) => pfad === `${p.art}.${s.id}`);
  return (
    <fieldset className="lay__gruppe">
      <legend>{name}</legend>
      <p className="lay__panelhinweis">
        {kopf ? "Unterkante des Kopfbereichs ab der oberen Blattkante"
          : "Höhe des Fußbereichs ab der unteren Blattkante"}
        , von {mmText(untergrenze)} (Seitenrand, mindestens {mmText(katalog.minMm)}) bis{" "}
        {mmText(katalog.maxMm)} mm, in Zehntelmillimetern. Leer = wie der Seitenrand
        ({mmText(rand)} mm).</p>
      <div className="lay__grafikfelder">
        {SEITEN.map((s) => {
          const pfad = `${p.art}.${s.id}`;
          return (
            <ZahlFeld key={s.id} id={feldId(pfad)} label={`${name} ${s.text}`} einheit="mm"
              wert={wertAn(p.entwurf, [p.art, s.id])} platzhalter={mmText(rand)}
              fehler={p.feldFehler[pfad]} systemwertKnopf
              onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, [p.art, s.id], neu))}
              onVerlassen={p.onFeldVerlassen} />
          );
        })}
      </div>
      <Befunde liste={restBefunde(p.feldFehler, p.art, behandelt)} />
    </fieldset>
  );
}

const BEGINN_TEXT = { fliessend: "im Fluss", folgeseite: "auf der ersten Folgeseite" } as const;

function Tabellenbeginn(p: PanelProps): ReactElement {
  const system = p.stand.katalog.positionstabelle.beginn;
  const andere = system === "fliessend" ? "folgeseite" : "fliessend";
  const roh = wertAn(p.entwurf, ["positionstabelle", "beginn"]);
  // Ein ausdrücklich gesetzter Systemwert zeigt sich als „Systemwert“ (kein eigener Wert).
  const wert = roh === andere ? andere : roh === system || roh === undefined ? "" : String(roh);
  // `positionstabelle.zebra` hat sein Feld im Register Tabelle und zählt dort.
  const behandelt = (pfad: string): boolean =>
    pfad === "positionstabelle.beginn" || pfad === "positionstabelle.zebra";
  return (
    <fieldset className="lay__gruppe">
      <legend>Positionstabelle</legend>
      <WahlFeld id={feldId("positionstabelle.beginn")} label="Positionstabelle beginnt"
        wert={wert}
        optionen={[
          { wert: "", text: `${BEGINN_TEXT[system]} (Systemwert)` },
          { wert: andere, text: BEGINN_TEXT[andere] },
          ...(wert !== "" && wert !== andere ? [{ wert, text: wert }] : []),
        ]}
        fehler={p.feldFehler["positionstabelle.beginn"]}
        onWahl={(w) => {
          p.onEntwurf(mitWert(p.entwurf, ["positionstabelle", "beginn"], w === "" ? undefined : w));
          p.onFeldVerlassen();
        }} />
      <p className="lay__panelhinweis">„Auf der ersten Folgeseite“: Seite 1 trägt Kopf,
        Anschreiben und Einleitung, die Tabelle beginnt oben auf Seite 2 — auch bei wenigen
        Positionen. Die Vorschau zeigt nur Seite 1 und simuliert den Umbruch nicht.</p>
      <Befunde liste={restBefunde(p.feldFehler, "positionstabelle", behandelt)} />
    </fieldset>
  );
}

const POSITION = { 2: ["links", "rechts"], 3: ["links", "Mitte", "rechts"] } as const;

/** Fuß in Spalten (Entscheid 75): Spaltenzahl, Breiten mit Ausgleich, Text mit Platzhaltern. */
function Fusszeile(p: PanelProps): ReactElement {
  const katalog = p.stand.katalog.fusszeile;
  const spalten = fussSpaltenAus(p.entwurf);
  const summe = spalten === null ? null : fussSumme(spalten);
  const summeOk = summe !== null && summe >= 99 && summe <= 101;
  const anzahl = spalten?.length ?? 0;
  const gueltigeZahl = anzahl === 2 || anzahl === 3;
  const doppelt = spalten !== null
    && spalten.some((s) => (s.text ?? "").includes("{firmenfuss}"))
    && spalten.some((s) => (s.text ?? "").includes("{fusszeile}"));

  function schreibe(neu: FussSpalte[], fertig: boolean): void {
    p.onEntwurf(mitFussSpalten(p.entwurf, neu));
    if (fertig) p.onFeldVerlassen();
  }
  const behandelt = (pfad: string): boolean => pfad === "fusszeile.spalten"
    || /^fusszeile\.spalten\[\d+\]\.(breite|text)$/.test(pfad);
  const listeFehler = p.feldFehler["fusszeile.spalten"];

  return (
    <fieldset className="lay__gruppe">
      <legend>Fußzeile</legend>
      <WahlFeld id={feldId("fusszeile.spalten")} label="Fuß in Spalten"
        wert={spalten === null ? "" : String(anzahl)}
        optionen={[
          { wert: "", text: "Systemfuß (drei Randboxen)" },
          { wert: "2", text: "2 Spalten" }, { wert: "3", text: "3 Spalten" },
          ...(spalten !== null && !gueltigeZahl
            ? [{ wert: String(anzahl), text: `${anzahl} Spalten (ungültig)` }] : []),
        ]}
        fehler={listeFehler}
        onWahl={(w) => {
          p.onEntwurf(mitFussSpaltenzahl(p.entwurf, w === "2" ? 2 : w === "3" ? 3 : null,
                                          katalog));
          p.onFeldVerlassen();
        }} />
      {spalten === null ? (
        <p className="lay__panelhinweis">Ohne Spalten steht der heutige Fuß: Firmen-Fußzeile
          links, Belegnummer in der Mitte, Seitenzahl rechts. Mit „2 Spalten“ oder „3 Spalten“
          legst du die Texte selbst fest; die Voreinstellung bildet den heutigen Fuß nach.</p>
      ) : (
        <>
          <ol className="lay__spalten lay__fussspalten">
            {spalten.map((s, i) => {
              const pfad = `fusszeile.spalten[${i}]`;
              const lage = gueltigeZahl ? POSITION[anzahl as 2 | 3][i] : undefined;
              const breiteFehler = p.feldFehler[`${pfad}.breite`];
              const name = `Fußspalte ${i + 1}`;
              const breiteId = feldId(`${pfad}.breite`);
              return (
                <li key={i} className="lay__fussspalte-eintrag">
                  <div className="lay__eingabezeile">
                    <span className="lay__spaltenname">{name}{lage === undefined ? "" : ` (${lage})`}</span>
                    <input type="text" inputMode="decimal"
                      className="lay__eingabe lay__eingabe--schmal"
                      aria-label={`${name}: Breite`} id={breiteId}
                      value={zahlAus(s.breite, "%")} aria-invalid={breiteFehler !== undefined}
                      {...(breiteFehler === undefined ? {}
                        : { "aria-describedby": `${breiteId}-fehler` })}
                      onChange={(e) => schreibe(mitFussBreite(spalten, i, e.target.value), false)}
                      onBlur={p.onFeldVerlassen} />
                    <span className="lay__einheit">%</span>
                  </div>
                  {breiteFehler !== undefined && (
                    <span id={`${breiteId}-fehler`} className="lay__feldfehler" role="alert">
                      {breiteFehler}</span>
                  )}
                  <TextFeld id={feldId(`${pfad}.text`)} label={`${name}: Text`}
                    wert={s.text} systemwert="" mehrzeilig max={katalog.textMax}
                    systemwertKnopf={false} fehler={p.feldFehler[`${pfad}.text`]}
                    onWert={(neu) => schreibe(spalten.map((x, k) =>
                      (k !== i ? x : neu === undefined ? { breite: x.breite }
                        : { breite: x.breite, text: neu })), false)}
                    onVerlassen={p.onFeldVerlassen} />
                </li>
              );
            })}
          </ol>
          <p className={`lay__summe lay__summe--${summeOk ? "ok" : "fehler"}`}>
            Summe der Breiten: {summe === null ? "—" : `${summe.toLocaleString("de-DE")} %`}
            {summeOk ? "" : summe === null ? " — eine Breite ist keine gültige Zahl"
              : " — erlaubt sind 100 % (± 1)"}
          </p>
          {listeFehler !== undefined && (
            <span className="lay__feldfehler" role="alert">{listeFehler}</span>
          )}
          <p className="lay__panelhinweis">Die Breite einer Spalte gleicht die rechts anliegende
            aus, bei der ganz rechten die ganz linke; Werte mit einer Nachkommastelle. Die
            Ausrichtung folgt der Lage: links, Mitte (bei drei Spalten), rechts. Mit ⏎ im Text
            beginnt eine neue Zeile.</p>
          <ul className="lay__hilfe lay__platzhalter" aria-label="Platzhalter">
            {PLATZHALTER.map((h) => (
              <li key={h.name}><code>{h.name}</code> — {h.text}</li>
            ))}
          </ul>
          <p className="lay__panelhinweis">Unten bleiben 5 mm Druckrand frei; was höher ist als
            der Fußbereich minus 5 mm, schneidet die Engine ab. Ein Firmenfuß im Drittel braucht auf
            Seite 1 etwa {katalog.fussbereichMm} mm Fußbereich.</p>
          {doppelt && (
            <p className="lay__meldung lay__meldung--schlecht" role="alert">
              {"{firmenfuss}"} enthält auf Seite 1 schon die Fußzeile — zusammen mit {"{fusszeile}"}{" "}
              steht sie dort doppelt.</p>
          )}
        </>
      )}
      <Befunde liste={restBefunde(p.feldFehler, "fusszeile", behandelt)} />
      {spalten !== null && (
        <div className="lay__werkzeuge">
          <button type="button" className="lay__knopf"
            onClick={() => { p.onEntwurf(mitFussSpaltenzahl(p.entwurf, null, katalog));
                             p.onFeldVerlassen(); }}>Systemfuß</button>
        </div>
      )}
    </fieldset>
  );
}

function SeiteEtappe2(p: PanelProps): ReactElement {
  return (
    <>
      <Bereich {...p} art="kopfbereich" />
      <Bereich {...p} art="fussbereich" />
      <Tabellenbeginn {...p} />
      <Fusszeile {...p} />
    </>
  );
}
